import React from 'react';
import { Alert, Spin } from 'antd';
import { useParams } from 'react-router-dom';
import { fetchSqlDataWithQuery } from '../../../services/api';
import type { MatLogisticShipment } from '../../../types';
import { MatLogShipmentUpsert } from './MatLogShipmentCreate';

const MatLogShipmentEdit: React.FC = () => {
  const { shipment_id } = useParams<{ shipment_id: string }>();
  const [loading, setLoading] = React.useState(true);
  const [shipment, setShipment] = React.useState<MatLogisticShipment | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!shipment_id) {
        setLoading(false);
        setError('Missing shipment_id in route');
        return;
      }
      try {
        setLoading(true);
        setError(null);
        setShipment(null);

        const sid = String(shipment_id).replace(/'/g, "''");
        const sql = `select * from public.mat_logistic_shipments where shipment_id = '${sid}' limit 1`;
        const res = await fetchSqlDataWithQuery(sql);
        const rows = Array.isArray(res) ? res : (Array.isArray((res as any)?.data) ? (res as any).data : []);
        const first = rows && rows[0] ? (rows[0] as MatLogisticShipment) : null;

        if (!cancelled) {
          setShipment(first);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message || 'Failed to load shipment');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [shipment_id]);

  if (!shipment_id) {
    return <Alert type="error" showIcon message="Missing shipment_id in route" style={{ margin: 8 }} />;
  }

  if (loading) {
    return (
      <div style={{ padding: 24, display: 'flex', justifyContent: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return <Alert type="error" showIcon message={error} style={{ margin: 8 }} />;
  }

  if (!shipment) {
    return <Alert type="warning" showIcon message="Shipment not found" style={{ margin: 8 }} />;
  }

  const isCancelled = String((shipment as any)?.status ?? '').trim().toLowerCase() === 'cancelled';

  return <MatLogShipmentUpsert mode="edit" initialShipment={shipment} readOnly={isCancelled} />;
};

export default MatLogShipmentEdit;
